import { MouseEvent, useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";
import { Link } from "@/components/LocaleLink";

// Labels sit just outside the dial (ticks end at r=186 of a 400 box, the
// label frame is at r≈196), so nothing crosses them: top, right, bottom, left
const LABEL_POS = [
  "left-1/2 -translate-x-1/2 top-0 -translate-y-full pb-1",
  "left-full top-1/2 -translate-y-1/2 pl-2",
  "left-1/2 -translate-x-1/2 bottom-0 translate-y-full pt-0.5",
  "right-full top-1/2 -translate-y-1/2 pr-2",
];

const R = 150;
const C = 2 * Math.PI * R;
// Each quarter: the signal rests on a stage, then glides to the next one
const DWELL_MS = 1800;
const MOVE_MS = 2600;
const QUARTER_MS = DWELL_MS + MOVE_MS;

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// Tail segments: [length as share of circumference, opacity]
const TAIL: [number, number][] = [
  [0.16, 0.08],
  [0.09, 0.22],
  [0.035, 0.9],
];

export interface LoopImage {
  src: string;
  caption: string;
  lang?: string;
  /** Entry page the photo belongs to. */
  href?: string;
}

/**
 * Material life-cycle ring (grow → make → use → return). A signal rests on
 * each stage, then glides on; the photo at the centre (real source images
 * only) changes with the stage.
 */
/** `images` holds one entry per stage (grow, make, use, return); null = no photo. */
const CircularityLoop = ({ images }: { images: (LoopImage | null)[] }) => {
  const { m, locale } = useI18n();
  const signalRef = useRef<SVGGElement>(null);
  const tiltRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState(0);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced.current) return;
    let frame = 0;
    let last = -1;
    const start = performance.now() + 900; // let the ring draw in first
    const tick = (now: number) => {
      const elapsed = Math.max(0, now - start);
      const q = Math.floor(elapsed / QUARTER_MS);
      const inQuarter = elapsed - q * QUARTER_MS;
      const move = inQuarter <= DWELL_MS ? 0 : easeInOutCubic((inQuarter - DWELL_MS) / MOVE_MS);
      signalRef.current?.setAttribute("transform", `rotate(${(q + move) * 90} 200 200)`);
      if (q !== last) {
        last = q;
        setStep(q);
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const active = step % 4;
  const current = images[active] ?? null;

  const onMove = (e: MouseEvent<HTMLDivElement>) => {
    if (reduced.current || !tiltRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    tiltRef.current.style.transform = `perspective(1200px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 6).toFixed(2)}deg)`;
  };
  const onLeave = () => {
    if (tiltRef.current) tiltRef.current.style.transform = "perspective(1200px) rotateX(0deg) rotateY(0deg)";
  };

  return (
    <div onMouseMove={onMove} onMouseLeave={onLeave} className="relative mx-auto w-full max-w-[400px]">
      <div ref={tiltRef} className="transition-transform duration-700 ease-out will-change-transform">
        <figure className="relative aspect-square" aria-label={m.loop.aria}>
          <svg viewBox="0 0 400 400" className="absolute inset-0 h-full w-full overflow-visible" aria-hidden>
            {/* Dial ticks */}
            {Array.from({ length: 120 }).map((_, i) => {
              const major = i % 30 === 0;
              const a = (i * 3 - 90) * (Math.PI / 180);
              const r1 = major ? 176 : 180;
              const r2 = 186;
              return (
                <line
                  key={i}
                  x1={200 + r1 * Math.cos(a)}
                  y1={200 + r1 * Math.sin(a)}
                  x2={200 + r2 * Math.cos(a)}
                  y2={200 + r2 * Math.sin(a)}
                  className={cn("loop-tick", major ? "stroke-muted-foreground/50" : "stroke-border")}
                  strokeWidth={major ? 1.2 : 1}
                  style={{ animationDelay: `${i * 6}ms` }}
                />
              );
            })}

            {/* Main ring draws itself in */}
            <circle
              cx="200"
              cy="200"
              r={R}
              fill="none"
              className="loop-draw stroke-foreground/15"
              strokeWidth="1"
              strokeDasharray={C}
              style={{ ["--loop-c" as string]: C }}
              transform="rotate(-90 200 200)"
            />

            {/* Signal with a fading tail */}
            <g ref={signalRef} className="loop-fade" transform="rotate(0 200 200)">
              {TAIL.map(([len, opacity]) => (
                <circle
                  key={len}
                  cx="200"
                  cy="200"
                  r={R}
                  fill="none"
                  className="stroke-primary"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeDasharray={`${C * len} ${C}`}
                  strokeDashoffset={C * len}
                  transform="rotate(-90 200 200)"
                  opacity={opacity}
                />
              ))}
              <circle cx="200" cy={200 - R} r="9" className="fill-primary" opacity="0.14" />
              <circle cx="200" cy={200 - R} r="3.5" className="fill-primary" />
            </g>

            {/* Stage nodes; the active one sends out a single soft ripple */}
            {[0, 90, 180, 270].map((deg, i) => {
              const a = ((deg - 90) * Math.PI) / 180;
              const x = 200 + R * Math.cos(a);
              const y = 200 + R * Math.sin(a);
              const on = active === i;
              return (
                <g key={deg}>
                  {on && <circle key={`ripple-${step}`} cx={x} cy={y} r="4" className="loop-ripple fill-none stroke-primary" strokeWidth="1" />}
                  <circle
                    cx={x}
                    cy={y}
                    r="4"
                    strokeWidth="1.2"
                    className={cn("transition-colors duration-700", on ? "fill-primary stroke-primary" : "fill-background stroke-muted-foreground/60")}
                  />
                </g>
              );
            })}
          </svg>

          {/* Stage names */}
          <div className="absolute inset-[1%]">
            {m.loop.stages.map((label, i) => (
              <span
                key={label}
                className={cn(
                  "loop-fade absolute whitespace-nowrap font-display text-base sm:text-lg leading-none transition-colors duration-700",
                  locale !== "zh" && "italic",
                  LABEL_POS[i],
                  active === i ? "text-foreground" : "text-muted-foreground/60",
                )}
                style={{ animationDelay: `${700 + i * 120}ms` }}
              >
                {label}
              </span>
            ))}
          </div>

          {/* Real source photos, changing with each stage */}
          <div className="loop-fade absolute inset-[27%] overflow-hidden rounded-full bg-muted shadow-[0_24px_48px_-28px_rgba(0,0,0,0.35)]" style={{ animationDelay: "500ms" }}>
            {images.map((img, i) =>
              img ? (
                <img
                  key={`${i}-${img.src}`}
                  src={img.src}
                  alt=""
                  aria-hidden
                  className={cn(
                    "absolute inset-0 h-full w-full object-cover transition-[opacity,transform] ease-out",
                    i === active ? "opacity-100 scale-100 duration-[1400ms]" : "opacity-0 scale-[1.06] duration-[1400ms]",
                  )}
                />
              ) : null,
            )}
          </div>
        </figure>
      </div>

      <figcaption className="mt-12 text-center">
        {current ? (
          current.href ? (
            <Link
              key={current.caption}
              to={current.href}
              lang={current.lang}
              className="loop-caption block text-sm text-foreground/80 underline decoration-border underline-offset-4 hover:text-primary hover:decoration-primary transition-colors"
            >
              {current.caption}
            </Link>
          ) : (
            <span key={current.caption} lang={current.lang} className="loop-caption block text-sm text-foreground/80">
              {current.caption}
            </span>
          )
        ) : (
          <span className="block text-sm">&nbsp;</span>
        )}
        <span className="mt-1 block text-xs text-muted-foreground">{m.loop.footnote}</span>
      </figcaption>
    </div>
  );
};

export default CircularityLoop;
